import { Attachments } from '../attachments/Attachments'
import { useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useLocation, useParams } from 'react-router-dom'
import type { Issue } from '../api/types'
import { Loading, NotFound } from '../components/layout'
import { InlineText } from '../components/InlineText'
import { QuestionCard } from '../questions/QuestionCard'
import { IssueQueue } from '../queue/IssueQueue'
import { IssueSessionAction, IssueSessions } from '../sessions/IssueSessions'
import { useBoardState, useStore } from '../store/context'
import { questionsOf, recordOf } from '../store/store'
import { PARTS } from '../vocab'
import { IssuePart } from './IssuePart'
import { IssueProperties } from './IssueProperties'
import { IssueComments, IssueRecord } from './IssueRecord'
import { IssueRevisions } from './IssueRevisions'
import { IssueRelations } from './IssueRelations'
import { IssueRuns } from './IssueRuns'
import { IssueSubIssues } from './IssueSubIssues'
import { IssueComment } from './IssueComment'
import { useDocumentTitle } from '../components/useDocumentTitle'
import { isUuid, readKey, sameKey } from '../lib/keys'
import { issuePath } from '../plans/html'
import { pullsOf } from '../lib/releases'

/**
 * The key of the issue that the server says `ref` names, when the board holds none by that id or key: a name it had on the board
 * it was imported from, an old id or the key it was moved from (Poulpe-461). `undefined` while the server is asked, null when it
 * names none. Only an id or a key is asked, and the server finds it in this workspace alone.
 */
function useOldName(ref: string, asked: boolean): string | null | undefined {
  const store = useStore()
  const [found, setFound] = useState<{ ref: string; key: string | null } | null>(null)
  useEffect(() => {
    if (!asked || !(isUuid(ref) || readKey(ref))) return
    let current = true
    store.search(ref, { all: true, limit: 1 }).then(
      (answer) => current && setFound({ ref, key: answer.jump?.kind === 'issue' ? answer.jump.key : null }),
      () => current && setFound({ ref, key: null }),
    )
    return () => {
      current = false
    }
  }, [store, ref, asked])
  if (!asked || !(isUuid(ref) || readKey(ref))) return null
  return found?.ref === ref ? found.key : undefined
}

/** An issue by its key: `/Poulpe-331`, in whatever case, with or without its number's leading zeros; or by its id. Either leads to its key; so does a name it had before the import. */
export function IssuePage() {
  const { key = '' } = useParams()
  const { search, hash } = useLocation()
  const { issues } = useBoardState()
  const store = useStore()
  const issue = useMemo(() => [...issues.values()].find((i) => sameKey(i.key, key) || i.id.toLowerCase() === key.toLowerCase()), [issues, key])
  const projectId = issue?.project
  // The board, from here, shows the issue's project.
  useEffect(() => {
    if (projectId) store.selectProject(projectId)
  }, [store, projectId])
  const named = useOldName(key, !issue)
  if (!issue) {
    if (named === undefined) return <Loading />
    // A key the board lacks leads nowhere new: the server's own key, only when it is another address.
    if (named && named.toLowerCase() !== key.toLowerCase()) return <Navigate to={`/${named}${search}${hash}`} replace />
    return <NotFound what="issue" />
  }
  if (issue.key !== key) return <Navigate to={`/${issue.key}${search}${hash}`} replace />
  // Keyed, so an edit under way never follows to another issue.
  return <IssueView key={issue.id} issue={issue} />
}

function IssueView({ issue }: { issue: Issue }) {
  useDocumentTitle(`${issue.key} ${issue.title}`)
  const store = useStore()
  const { entries, issues, questions } = useBoardState()
  const parent = issue.parent ? issues.get(issue.parent) : undefined
  const record = useMemo(() => recordOf(entries, issue.id), [entries, issue.id])
  const asked = useMemo(() => questionsOf(questions, issue.id), [questions, issue.id])
  const [addedComment, setAddedComment] = useState<string | null>(null)
  return (
    <article className="issue-page">
      <header className="issue-head">
        <nav className="breadcrumbs" aria-label="Breadcrumb">
          <Link to="/board">Board</Link>
          {parent && (
            <>
              <span aria-hidden="true">›</span>
              <Link to={issuePath(parent)} title={parent.title}>
                {parent.key}
              </Link>
            </>
          )}
          <span aria-hidden="true">›</span>
          <span aria-current="page">{issue.key}</span>
        </nav>
        <InlineText
          as="h1"
          className="issue-title"
          value={issue.title}
          onSave={(title) => void store.updateIssue(issue.id, { title })}
          placeholder="Untitled"
          name="title"
          maxLength={500}
          required
          multiline
        />
        <IssueSessionAction issue={issue} />
        <InlineText
          as="p"
          className="issue-summary"
          value={issue.summary}
          onSave={(summary) => void store.updateIssue(issue.id, { summary })}
          placeholder="Add a one-line summary"
          name="summary"
          maxLength={200}
        />
      </header>
      <aside className="issue-side" aria-label="Properties">
        <IssueProperties issue={issue} />
      </aside>
      <div className="issue-body">
        {issue.archived_at && <p className="notice">This issue is archived.</p>}
        <IssueQueue issueId={issue.id} />
        {asked.length > 0 && (
          <section className="section issue-questions" aria-label="Questions">
            <header className="section-header"><h2>Questions<span className="section-count">{asked.length}</span></h2></header>
            {asked.map((question) => (
              <QuestionCard key={question.id} question={question} />
            ))}
          </section>
        )}
        {PARTS.map((part) => (
          <IssuePart key={part} issue={issue} part={part} />
        ))}
        <IssuePulls issue={issue} />
        <IssueSubIssues issue={issue} />
        <IssueRelations issue={issue} />
        <Attachments target={{ issue: issue.id }} />
        <IssueSessions issue={issue} />
        <IssueRuns issue={issue} />
        <IssueRevisions issue={issue} />
        <IssueComments entries={record} reveal={addedComment} onReplied={setAddedComment} />
        <IssueComment issueId={issue.id} onAdded={setAddedComment} />
        <IssueRecord entries={record} />
      </div>
    </article>
  )
}

/** An imported issue's pull requests, where today's page says its GitHub facts, each `#N` linked to GitHub (Poulpe-500). */
export function IssuePulls({ issue }: { issue: Issue }) {
  const pulls = pullsOf(issue)
  if (!pulls.length) return null
  return <section className="issue-github"><h2>GitHub</h2><ul>{pulls.map((pull) => <li key={pull.url}>
    <a href={pull.url} target="_blank" rel="noreferrer" aria-label={`Pull request ${pull.number} on GitHub`}>#{pull.number}</a>
  </li>)}</ul></section>
}
